import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useLocation } from "wouter";
import { Button } from "../components/Button";
import { Card } from "../components/Card";
import { EmptyState } from "../components/EmptyState";
import { Field } from "../components/Field";
import { Modal } from "../components/Modal";
import { Skeleton } from "../components/Spinner";
import { Table, type Column } from "../components/Table";
import { useToast } from "../components/Toast";
import { ApiError, apiGet, apiSend, serverBesked } from "../lib/customFetch";
import { ROLLE_TEKST, useMe, type UserRole } from "../lib/useMe";
import styles from "./MedarbejderePage.module.css";

type Status =
  | "invite_afventer"
  | "aktiv"
  | "adgang_udloebet"
  | "deaktiveret";

interface Employee {
  id: string;
  navn: string;
  status: Status;
  mode: "stempel" | "standard";
  oprettetAt: string;
  loen: { medarbejdernummer: string | null; aktiv: boolean };
}

interface InviteInfo {
  userId: string;
  navn: string;
  inviteUrl: string;
  qrDataUrl: string;
  udloeberAt: string;
  mailStatus?: "sendt" | "fejlet" | "deaktiveret" | "ikke_anmodet";
}

interface AccessOverview {
  users: Array<{
    id: string;
    navn: string;
    email: string;
    rolle: "ejer" | "administrator" | "bogholder";
    aktiv: boolean;
    current: boolean;
  }>;
  invites: Array<{
    id: string;
    email: string;
    rolle: "administrator" | "bogholder";
    udloeberAt: string;
  }>;
}

interface AdministrativeInvite {
  email: string;
  rolle: "administrator" | "bogholder";
  inviteUrl: string;
  udloeberAt: string;
}

type ListState =
  | { status: "loading" }
  | { status: "ok"; rows: Employee[] }
  | { status: "failed" };

const STATUS_TEKST: Record<Status, string> = {
  invite_afventer: "Invitation afventer",
  aktiv: "Aktiv",
  adgang_udloebet: "Adgang udløbet",
  deaktiveret: "Deaktiveret",
};

const STATUS_KLASSE: Record<Status, string> = {
  invite_afventer: styles.statusAfventer ?? "",
  aktiv: styles.statusAktiv ?? "",
  adgang_udloebet: styles.statusUdloebet ?? "",
  deaktiveret: styles.statusDeaktiveret ?? "",
};

function formatDato(iso: string): string {
  return new Date(iso).toLocaleDateString("da-DK", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function MedarbejderePage() {
  const { me } = useMe();
  const toast = useToast();
  const [liste, setListe] = useState<ListState>({ status: "loading" });
  const [opretOpen, setOpretOpen] = useState(false);
  const [rediger, setRediger] = useState<Employee | null>(null);
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [loftOpen, setLoftOpen] = useState(false);

  const hentListe = useCallback(() => {
    setListe({ status: "loading" });
    apiGet<Employee[]>("/api/employees")
      .then((rows) => setListe({ status: "ok", rows }))
      .catch(() => setListe({ status: "failed" }));
  }, []);

  useEffect(() => {
    hentListe();
  }, [hentListe]);

  const handling = (id: string, action: string, besked: string) => {
    apiSend("POST", `/api/employees/${id}/${action}`)
      .then(() => {
        toast.show(besked, "success");
        hentListe();
      })
      .catch((err: unknown) => {
        // 9-loftet (fase 3a): reaktivering kan ramme invarianten.
        if (err instanceof ApiError && err.status === 409) {
          setLoftOpen(true);
        } else {
          toast.show("Noget gik galt — prøv igen.", "error");
        }
      });
  };

  const reinvite = (emp: Employee) => {
    apiSend<InviteInfo>("POST", `/api/employees/${emp.id}/reinvite`)
      .then((info) => {
        setInvite(info);
        hentListe();
      })
      .catch(() => toast.show("Kunne ikke oprette en ny invitation.", "error"));
  };

  const columns: Column<Employee>[] = [
    { key: "navn", header: "Navn", mobileLabel: "Navn", render: (r) => r.navn },
    {
      key: "type",
      header: "Type",
      mobileLabel: "Registrering",
      render: (r) => (
        <span
          className={`${styles.status} ${r.mode === "standard" ? styles.statusStandard ?? "" : styles.statusStempel ?? ""}`}
        >
          {r.mode === "standard" ? "Standarddag" : "Stempel"}
        </span>
      ),
    },
    {
      key: "loen",
      header: "Lønfil",
      mobileLabel: "Lønfil",
      render: (r) => (
        <span
          className={`${styles.status} ${r.loen.aktiv ? styles.statusAktiv ?? "" : styles.statusStempel ?? ""}`}
        >
          {r.loen.aktiv
            ? r.loen.medarbejdernummer === null
              ? "Mangler nummer"
              : `Danløn · ${r.loen.medarbejdernummer}`
            : "Udeladt"}
        </span>
      ),
    },
    {
      key: "status",
      header: "Status",
      mobileLabel: "Status",
      render: (r) => (
        <span className={`${styles.status} ${STATUS_KLASSE[r.status]}`}>
          {STATUS_TEKST[r.status]}
        </span>
      ),
    },
    {
      key: "oprettet",
      header: "Oprettet",
      mobileLabel: "Oprettet",
      render: (r) => formatDato(r.oprettetAt),
    },
    {
      key: "handlinger",
      header: <span className="visually-hidden">Handlinger</span>,
      mobileLabel: "Handlinger",
      align: "right",
      render: (r) => (
        <div className={styles.actions}>
          <div className={styles.primaryActions}>
            <Button variant="ghost" onClick={() => setRediger(r)}>
              Redigér
            </Button>
            <Button variant="ghost" onClick={() => reinvite(r)}>
              Nyt link
            </Button>
            {r.status === "deaktiveret" && (
              <Button
                variant="secondary"
                onClick={() => handling(r.id, "reactivate", `${r.navn} er genaktiveret.`)}
              >
                Genaktivér
              </Button>
            )}
          </div>
          {r.status !== "deaktiveret" && (
            <details className={styles.secondaryActions}>
              <summary>Adgang</summary>
              <div className={styles.secondaryActionsPanel}>
                <span>Stop medarbejderens adgang</span>
                <Button
                  variant="destructive"
                  onClick={() => handling(r.id, "deactivate", `${r.navn} er deaktiveret.`)}
                >
                  Deaktivér
                </Button>
              </div>
            </details>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <Card
        title="Medarbejdere"
        actions={
          liste.status === "ok" && liste.rows.length > 0 ? (
            <Button onClick={() => setOpretOpen(true)}>Opret medarbejder</Button>
          ) : undefined
        }
      >
        {liste.status === "loading" && (
          // Skeleton med tabellens kendte form — intet layout-spring.
          <div className={styles.listeSkelet} aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height="3rem" />
            ))}
          </div>
        )}
        {liste.status === "failed" && (
          <EmptyState
            title="Kunne ikke hente medarbejdere"
            description="Tjek din forbindelse og prøv igen."
            cta={<Button onClick={hentListe}>Prøv igen</Button>}
          />
        )}
        {liste.status === "ok" && liste.rows.length === 0 && (
          <EmptyState
            illustration="👷"
            title="Opret din første medarbejder"
            description="Medarbejderen kommer i gang med et link eller en QR-kode på under et minut — ingen adgangskode og ingen app-installation."
            cta={
              <Button onClick={() => setOpretOpen(true)}>
                Opret medarbejder
              </Button>
            }
          />
        )}
        {liste.status === "ok" && liste.rows.length > 0 && (
          <Table
            columns={columns}
            rows={liste.rows}
            rowKey={(r) => r.id}
            emptyTitle="Ingen medarbejdere"
            mobileCards
          />
        )}
      </Card>

      {me?.rolle === "ejer" && <AccessAdministration />}

      {opretOpen && (
        <OpretModal
          onClose={() => setOpretOpen(false)}
          onCreated={(info) => {
            setOpretOpen(false);
            setInvite(info);
            hentListe();
          }}
          onLoft={() => {
            setOpretOpen(false);
            setLoftOpen(true);
          }}
        />
      )}

      {loftOpen && <LoftModal onClose={() => setLoftOpen(false)} />}

      {rediger !== null && (
        <RedigerModal
          employee={rediger}
          onClose={() => setRediger(null)}
          onSaved={() => {
            setRediger(null);
            toast.show("Medarbejderen er gemt.", "success");
            hentListe();
          }}
          onReload={hentListe}
        />
      )}

      {invite !== null && (
        <InviteModal invite={invite} onClose={() => setInvite(null)} />
      )}
    </>
  );
}

type AccessUser = AccessOverview["users"][number];
type AccessChange = {
  user: AccessUser;
  patch: { rolle?: "ejer" | "administrator" | "bogholder"; aktiv?: boolean };
  description: string;
};

function AccessAdministration() {
  const toast = useToast();
  const [, navigate] = useLocation();
  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "failed" }
    | { status: "ok"; data: AccessOverview }
  >({ status: "loading" });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [createdInvite, setCreatedInvite] = useState<AdministrativeInvite | null>(null);
  const [change, setChange] = useState<AccessChange | null>(null);
  const [inviteToRevoke, setInviteToRevoke] = useState<AccessOverview["invites"][number] | null>(null);
  const [changeError, setChangeError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setState({ status: "loading" });
    apiGet<AccessOverview>("/api/access")
      .then((data) => setState({ status: "ok", data }))
      .catch(() => setState({ status: "failed" }));
  }, []);

  useEffect(() => load(), [load]);

  const confirmChange = async () => {
    if (change === null) return;
    setBusy(true);
    setChangeError(null);
    try {
      await apiSend("PATCH", `/api/access/users/${change.user.id}`, change.patch);
      if (change.user.current) {
        navigate("/login?adgang=aendret", { replace: true });
        return;
      }
      toast.show("Adgangen er opdateret.", "success");
      setChange(null);
      load();
    } catch (error) {
      setChangeError(
        (error instanceof ApiError ? serverBesked(error) : null) ??
          "Kunne ikke ændre adgangen. Prøv igen.",
      );
    } finally {
      setBusy(false);
    }
  };

  const confirmRevoke = async () => {
    if (inviteToRevoke === null) return;
    setBusy(true);
    setChangeError(null);
    try {
      await apiSend("DELETE", `/api/access/invites/${inviteToRevoke.id}`);
      toast.show("Invitationen er trukket tilbage.", "success");
      setInviteToRevoke(null);
      load();
    } catch (error) {
      setChangeError(
        (error instanceof ApiError ? serverBesked(error) : null) ??
          "Kunne ikke trække invitationen tilbage. Prøv igen.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Adgang til administration"
      actions={
        state.status === "ok" ? (
          <Button onClick={() => setInviteOpen(true)}>Invitér adgang</Button>
        ) : undefined
      }
    >
      <p className={styles.accessIntro}>
        Giv administratorer adgang til driften, eller bogholderen en rolig
        læse- og eksportadgang. Kun ejere kan ændre disse adgange.
      </p>
      {state.status === "loading" && (
        <div className={styles.listeSkelet} aria-label="Henter adgange">
          <Skeleton height="3rem" />
          <Skeleton height="3rem" />
        </div>
      )}
      {state.status === "failed" && (
        <EmptyState
          title="Kunne ikke hente adgange"
          description="Ingen ændringer er foretaget. Prøv at hente listen igen."
          cta={<Button onClick={load}>Prøv igen</Button>}
        />
      )}
      {state.status === "ok" && (
        <div className={styles.accessSections}>
          <div className={styles.accessList} aria-label="Administrative brugere">
            {state.data.users.map((user) => (
              <article className={styles.accessRow} key={user.id}>
                <div className={styles.accessIdentity}>
                  <strong>{user.navn}</strong>
                  {user.current && <span>Din konto</span>}
                  <span>{user.email}</span>
                  <span className={`${styles.status} ${user.aktiv ? styles.statusAktiv ?? "" : styles.statusDeaktiveret ?? ""}`}>
                    {ROLLE_TEKST[user.rolle]} · {user.aktiv ? "Aktiv" : "Deaktiveret"}
                  </span>
                </div>
                <div className={styles.accessActions}>
                  <label>
                    <span className="visually-hidden">Rolle for {user.navn}</span>
                    <select
                      aria-label={`Rolle for ${user.navn}`}
                      value={user.rolle}
                      disabled={!user.aktiv}
                      onChange={(event) => {
                        const rolle = event.target.value as Exclude<UserRole, "medarbejder">;
                        setChangeError(null);
                        setChange({
                          user,
                          patch: { rolle },
                          description: user.current
                            ? `Skift din egen rolle fra ${ROLLE_TEKST[user.rolle].toLowerCase()} til ${ROLLE_TEKST[rolle].toLowerCase()}? Du bliver logget ud med det samme.`
                            : `Skift ${user.navn} fra ${ROLLE_TEKST[user.rolle].toLowerCase()} til ${ROLLE_TEKST[rolle].toLowerCase()}?`,
                        });
                      }}
                    >
                      <option value="ejer">Ejer</option>
                      <option value="administrator">Administrator</option>
                      <option value="bogholder">Bogholder</option>
                    </select>
                  </label>
                  <Button
                    variant={user.aktiv ? "ghost" : "secondary"}
                    onClick={() => {
                      setChangeError(null);
                      setChange({
                        user,
                        patch: { aktiv: !user.aktiv },
                        description: user.aktiv
                          ? user.current
                            ? "Deaktivér din konto? Du bliver logget ud med det samme. En anden ejer skal genaktivere den."
                            : `Deaktivér ${user.navn}? Personen bliver logget ud og mister straks adgangen.`
                          : `Genaktivér ${user.navn} med rollen ${ROLLE_TEKST[user.rolle].toLowerCase()}?`,
                      });
                    }}
                  >
                    {user.aktiv ? "Deaktivér" : "Genaktivér"}
                  </Button>
                </div>
              </article>
            ))}
          </div>
          {state.data.invites.length > 0 && (
            <div className={styles.pendingAccess}>
              <h3>Afventer aktivering</h3>
              {state.data.invites.map((invite) => (
                <div className={styles.pendingAccessRow} key={invite.id}>
                  <div>
                    <strong>{invite.email}</strong>
                    <span>
                      {ROLLE_TEKST[invite.rolle]} · udløber {formatDato(invite.udloeberAt)}
                    </span>
                  </div>
                  <Button variant="ghost" onClick={() => {
                    setChangeError(null);
                    setInviteToRevoke(invite);
                  }}>
                    Træk tilbage
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {inviteOpen && (
        <AdministrativeInviteModal
          onClose={() => setInviteOpen(false)}
          onCreated={(invite) => {
            setInviteOpen(false);
            setCreatedInvite(invite);
            load();
          }}
        />
      )}
      {createdInvite !== null && (
        <AdministrativeInviteResult
          invite={createdInvite}
          onClose={() => setCreatedInvite(null)}
        />
      )}
      {change !== null && (
        <Modal title="Bekræft adgangsændring" onClose={() => !busy && setChange(null)}>
          <div className={styles.createForm}>
            <p>{change.description}</p>
            {changeError !== null && <p className={styles.fejl} role="alert">{changeError}</p>}
            <div className={styles.loftKnapper}>
              <Button onClick={() => void confirmChange()} disabled={busy}>
                {busy ? "Gemmer…" : "Bekræft ændring"}
              </Button>
              <Button variant="secondary" onClick={() => setChange(null)} disabled={busy}>
                Annuller
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {inviteToRevoke !== null && (
        <Modal
          title="Træk invitation tilbage"
          onClose={() => !busy && setInviteToRevoke(null)}
        >
          <div className={styles.createForm}>
            <p>
              Linket til <strong>{inviteToRevoke.email}</strong> holder straks op
              med at virke.
            </p>
            {changeError !== null && <p className={styles.fejl} role="alert">{changeError}</p>}
            <div className={styles.loftKnapper}>
              <Button onClick={() => void confirmRevoke()} disabled={busy}>
                {busy ? "Trækker tilbage…" : "Træk tilbage"}
              </Button>
              <Button variant="secondary" onClick={() => setInviteToRevoke(null)} disabled={busy}>
                Behold invitationen
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
}

function AdministrativeInviteModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (invite: AdministrativeInvite) => void;
}) {
  const [email, setEmail] = useState("");
  const [rolle, setRolle] = useState<"administrator" | "bogholder">("bogholder");
  const [requestId] = useState(() => crypto.randomUUID());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onCreated(
        await apiSend<AdministrativeInvite>("POST", "/api/access/invites", {
          email: email.trim(),
          rolle,
          requestId,
        }),
      );
    } catch (caught) {
      setError(
        (caught instanceof ApiError ? serverBesked(caught) : null) ??
          "Kunne ikke oprette invitationen. Prøv igen.",
      );
      setBusy(false);
    }
  };

  return (
    <Modal title="Invitér til administration" onClose={onClose}>
      <form className={styles.createForm} onSubmit={(event) => void submit(event)}>
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          autoFocus
          required
        />
        <label className={styles.accessField}>
          <span>Adgangstype</span>
          <select value={rolle} onChange={(event) => setRolle(event.target.value as typeof rolle)}>
            <option value="bogholder">Bogholder · læs og eksportér</option>
            <option value="administrator">Administrator · administrér driften</option>
          </select>
        </label>
        <p className={styles.accessHint}>
          Rollen er bundet til invitationen. Modtageren vælger kun navn og adgangskode.
        </p>
        {error !== null && <p className={styles.fejl} role="alert">{error}</p>}
        <Button type="submit" disabled={busy}>{busy ? "Opretter…" : "Opret invitation"}</Button>
      </form>
    </Modal>
  );
}

function AdministrativeInviteResult({
  invite,
  onClose,
}: {
  invite: AdministrativeInvite;
  onClose: () => void;
}) {
  const toast = useToast();
  const copy = () => {
    navigator.clipboard.writeText(invite.inviteUrl)
      .then(() => toast.show("Link kopieret.", "success"))
      .catch(() => toast.show("Kunne ikke kopiere linket.", "error"));
  };
  return (
    <Modal title="Invitationen er klar" onClose={onClose}>
      <div className={styles.createForm}>
        <p>
          Del linket med <strong>{invite.email}</strong>. Det giver adgang som {ROLLE_TEKST[invite.rolle].toLowerCase()} og udløber {formatDato(invite.udloeberAt)}.
        </p>
        <div className={styles.linkRow}>
          <input className={styles.linkInput} readOnly value={invite.inviteUrl} aria-label="Administrativt invitationslink" onFocus={(event) => event.target.select()} />
          <Button variant="secondary" onClick={copy}>Kopiér</Button>
        </div>
        <Button onClick={onClose}>Færdig</Button>
      </div>
    </Modal>
  );
}

const UGEDAGE = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"] as const;
/** Dansk 37-timers standard: man–fre 7,4 t, fri lør/søn. */
const DEFAULT_TIMER = ["7.4", "7.4", "7.4", "7.4", "7.4", "0", "0"];

interface Arbejdstid {
  mode: "stempel" | "standard";
  gyldigFra: string | null;
  skema: number[];
}

function timerTilMinutter(t: string): number {
  return Math.round(Number(t.replace(",", ".")) * 60);
}

function RedigerModal({
  employee,
  onClose,
  onSaved,
  onReload,
}: {
  employee: Employee;
  onClose: () => void;
  onSaved: () => void;
  onReload: () => void;
}) {
  const [navn, setNavn] = useState(employee.navn);
  const [mode, setMode] = useState<"stempel" | "standard">(employee.mode);
  const [timer, setTimer] = useState<string[]>(DEFAULT_TIMER);
  const [loenAktiv, setLoenAktiv] = useState(employee.loen.aktiv);
  const [medarbejdernummer, setMedarbejdernummer] = useState(
    employee.loen.medarbejdernummer ?? "",
  );
  const [fejl, setFejl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Hent nuværende arbejdstid (mode + skema) til prefill.
  useEffect(() => {
    apiGet<Arbejdstid>(`/api/clockin/admin/arbejdstid/${employee.id}`)
      .then((a) => {
        setMode(a.mode);
        setTimer(a.skema.map((m) => (m / 60).toString()));
      })
      .catch(() => {
        /* Falder tilbage til employee.mode + dansk default. */
      });
  }, [employee.id]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (navn.trim().length === 0) {
      setFejl("Angiv medarbejderens navn.");
      return;
    }
    if (
      (loenAktiv && medarbejdernummer === "") ||
      (medarbejdernummer !== "" && !/^[0-9]{1,20}$/.test(medarbejdernummer))
    ) {
      setFejl(
        loenAktiv
          ? "Angiv medarbejdernummeret fra Danløn med kun cifre."
          : "Medarbejdernummeret må kun indeholde cifre.",
      );
      return;
    }
    setBusy(true);
    const gemArbejdstid = () =>
      apiSend("PUT", `/api/clockin/admin/arbejdstid/${employee.id}`, {
        mode,
        ...(mode === "standard"
          ? { skema: timer.map(timerTilMinutter) }
          : {}),
      });
    const gem = async () => {
      try {
        await apiSend("PATCH", `/api/employees/${employee.id}`, {
          navn: navn.trim(),
          loen: {
            aktiv: loenAktiv,
            medarbejdernummer:
              medarbejdernummer === "" ? null : medarbejdernummer,
          },
        });
      } catch (error) {
        setBusy(false);
        setFejl(
          (error instanceof ApiError ? serverBesked(error) : null) ??
            "Kunne ikke gemme — prøv igen.",
        );
        return;
      }
      try {
        await gemArbejdstid();
      } catch (error) {
        setBusy(false);
        onReload();
        const besked =
          (error instanceof ApiError ? serverBesked(error) : null) ??
          "Prøv igen eller ret den låste uge.";
        setFejl(
          `Navn og lønoplysninger er gemt, men arbejdstiden blev ikke gemt: ${besked}`,
        );
        return;
      }
      onSaved();
    };
    void gem();
  };

  return (
    <Modal title="Redigér medarbejder" onClose={onClose}>
      <form className={styles.createForm} onSubmit={submit}>
        <Field
          label="Navn"
          value={navn}
          onChange={(e) => setNavn(e.target.value)}
          autoFocus
          required
        />

        <fieldset className={styles.modeValg}>
          <legend className={styles.modeLegend}>Registrering</legend>
          <label className={styles.modeOption}>
            <input
              type="radio"
              name="mode"
              checked={mode === "stempel"}
              onChange={() => setMode("stempel")}
            />
            <span>
              <strong>Stempel</strong> — medarbejderen stempler ind og ud
            </span>
          </label>
          <label className={styles.modeOption}>
            <input
              type="radio"
              name="mode"
              checked={mode === "standard"}
              onChange={() => setMode("standard")}
            />
            <span>
              <strong>Standarddag</strong> — fast ugeskema, kun afvigelser
              registreres (funktionær)
            </span>
          </label>
        </fieldset>

        <fieldset className={styles.modeValg}>
          <legend className={styles.modeLegend}>Lønfil</legend>
          <label className={styles.modeOption}>
            <input
              type="checkbox"
              checked={loenAktiv}
              onChange={(event) => setLoenAktiv(event.target.checked)}
            />
            <span>
              <strong>Medtag i Danløn-fil</strong> — først når nummeret er
              kontrolleret mod medarbejderen i lønsystemet
            </span>
          </label>
          <Field
            label="Danløn-medarbejdernummer"
            inputMode="numeric"
            maxLength={20}
            placeholder="Fx 20"
            hint={
              loenAktiv
                ? "Skal være identisk med nummeret i Danløn."
                : "Gemmes til senere, men medarbejderen er udeladt fra filen."
            }
            value={medarbejdernummer}
            onChange={(event) => setMedarbejdernummer(event.target.value)}
          />
        </fieldset>

        {mode === "standard" && (
          <div className={styles.skemaEditor}>
            <p className={styles.skemaHint}>
              Timer pr. ugedag (netto). Gælder fremadrettet fra i dag.
            </p>
            <div className={styles.skemaGrid}>
              {UGEDAGE.map((dag, i) => (
                <label key={dag} className={styles.skemaDag}>
                  {dag}
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    max="24"
                    step="0.1"
                    value={timer[i]}
                    onChange={(e) =>
                      setTimer((prev) =>
                        prev.map((t, j) => (j === i ? e.target.value : t)),
                      )
                    }
                  />
                </label>
              ))}
            </div>
          </div>
        )}

        {fejl !== null && (
          <p className={styles.fejl} role="alert">
            {fejl}
          </p>
        )}
        <Button type="submit" disabled={busy}>
          {busy ? "Gemmer…" : "Gem"}
        </Button>
      </form>
    </Modal>
  );
}

function OpretModal({
  onClose,
  onCreated,
  onLoft,
}: {
  onClose: () => void;
  onCreated: (info: InviteInfo) => void;
  onLoft: () => void;
}) {
  const [navn, setNavn] = useState("");
  const [email, setEmail] = useState("");
  const [fejl, setFejl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [requestId] = useState(() => crypto.randomUUID());

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (navn.trim().length === 0) {
      setFejl("Angiv medarbejderens navn.");
      return;
    }
    setBusy(true);
    apiSend<InviteInfo>("POST", "/api/employees", {
      navn: navn.trim(),
      requestId,
      ...(email.trim().length > 0 ? { email: email.trim() } : {}),
    })
      .then(onCreated)
      .catch((err: unknown) => {
        setBusy(false);
        // 9-loftet (fase 3a): venlig blokering, ikke en fejl.
        if (err instanceof ApiError && err.status === 409) {
          onLoft();
        } else {
          setFejl("Kunne ikke oprette medarbejderen — prøv igen.");
        }
      });
  };

  return (
    <Modal title="Opret medarbejder" onClose={onClose}>
      <form className={styles.createForm} onSubmit={submit}>
        <Field
          label="Navn"
          placeholder="Fx Jonas Jensen"
          value={navn}
          onChange={(e) => setNavn(e.target.value)}
          autoFocus
          required
        />
        <Field
          label="Email (valgfrit)"
          type="email"
          autoComplete="email"
          hint="Hvis mails er slået til, sender vi invitationslinket direkte. Link og QR-kode vises altid her."
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        {fejl !== null && (
          <p className={styles.fejl} role="alert">
            {fejl}
          </p>
        )}
        <Button type="submit" disabled={busy}>
          {busy ? "Opretter…" : "Opret og vis invitation"}
        </Button>
      </form>
    </Modal>
  );
}

/**
 * 9-loftet ramt (fase 3a): venlig blokering med AppSmeden-CTA —
 * håndværker-til-håndværker, aldrig pushy.
 */
function LoftModal({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="I er ved at vokse ud af gratis-udgaven" onClose={onClose}>
      <div className={styles.loftIndhold}>
        <p>
          Gratis-udgaven dækker op til <strong>9 aktive medarbejdere</strong>{" "}
          — og I har brugt alle pladserne. I kan altid frigøre en plads ved
          at deaktivere en medarbejder, der er stoppet.
        </p>
        <p>
          Flere end 9? Så er I klar til jeres eget system — skræddersyet
          til jeres hverdag, med det I mangler. Snak med os, så finder vi
          ud af det sammen.
        </p>
        <div className={styles.loftKnapper}>
          <Button
            onClick={() =>
              window.open("https://appsmeden.dk", "_blank", "noopener")
            }
          >
            Snak med AppSmeden
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Luk
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function InviteModal({
  invite,
  onClose,
}: {
  invite: InviteInfo;
  onClose: () => void;
}) {
  const toast = useToast();

  const kopier = () => {
    navigator.clipboard
      .writeText(invite.inviteUrl)
      .then(() => toast.show("Link kopieret.", "success"))
      .catch(() => toast.show("Kunne ikke kopiere linket.", "error"));
  };

  return (
    <Modal title={`Invitér ${invite.navn}`} onClose={onClose}>
      <div className={styles.inviteView}>
        {invite.mailStatus === "sendt" && (
          <p className={styles.mailSuccess} role="status">
            Invitationsmailen er sendt. Link og QR-kode kan stadig deles manuelt.
          </p>
        )}
        {invite.mailStatus === "fejlet" && (
          <p className={styles.mailWarning} role="status">
            Medarbejderen er oprettet, men mailen kunne ikke sendes. Del linket
            eller QR-koden nedenfor.
          </p>
        )}
        {invite.mailStatus === "deaktiveret" && (
          <p className={styles.mailWarning} role="status">
            Mail er ikke aktiveret. Del linket eller QR-koden nedenfor.
          </p>
        )}
        <p className={styles.inviteHint}>
          Bed {invite.navn} scanne koden med telefonens kamera — så er
          telefonen koblet på med det samme.
        </p>
        <img
          className={styles.qr}
          src={invite.qrDataUrl}
          alt={`QR-kode med invitationslink til ${invite.navn}`}
        />
        <div className={styles.linkRow}>
          <input
            className={styles.linkInput}
            readOnly
            value={invite.inviteUrl}
            aria-label="Invitationslink"
            onFocus={(e) => e.target.select()}
          />
          <Button variant="secondary" onClick={kopier}>
            Kopiér
          </Button>
        </div>
        <p className={styles.udloeb}>
          Linket udløber {formatDato(invite.udloeberAt)}. Mister{" "}
          {invite.navn} telefonen, så lav en ny invitation — den gamle adgang
          lukkes automatisk.
        </p>
      </div>
    </Modal>
  );
}
